/**
 * A scheme allowlist for URLs the app stores and renders (NM-122).
 *
 * Source URLs come from an AI provider's output (or the import door), so an
 * untrusted party chooses the string, and the client renders it straight into an
 * `<a href>` (`feed.tsx`). A `javascript:` or `data:` URL there is a clickable
 * script-execution / phishing vector. Server-side link verification
 * (`images/safety.ts` via `verify-links.ts`) already drops bad-scheme URLs it
 * *fetches*, but it is off under `--ai-test`, absent on the import path, and
 * skipped for zero-source stories — so it cannot be the only guard between a bad
 * URL and the DOM.
 *
 * This is the client-safe half of that defense: a pure check on the URL's
 * scheme, using the WHATWG `URL` parser available in both Node and the browser
 * (no `node:` imports), so the same rule runs at the parse/import schemas
 * (`prompt.ts`, `schemas.ts`) and at the render site (`feed.tsx`). It is
 * deliberately narrower than `staticUrlRejection` — that one also blocks
 * loopback/private hosts for *fetching*, which needs `node:net`/`node:dns` and
 * would not bundle to the client; a stored link to a private host is harmless to
 * *display*, so scheme is the property that matters here.
 */

/** Whether `raw` is an `http(s)` URL — the only schemes safe to store or render. */
export function isHttpUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
}

/** The URL when it is a safe `http(s)` link, else `undefined` — for `href` binding. */
export function safeHref(raw: string): string | undefined {
  return isHttpUrl(raw) ? raw : undefined;
}
